import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AdCreditTransactions from "@/components/ad-credits/transactions";
import UpdateUserCreditModal from "./UpdateUserCreditModal";
import { Accordion, Button } from "rizzui";
import StatBox from "@/components/ui/stat-box";
import UpdateUserCreditPackageForm from "./UpdateUserCreditPackageForm";
import AdCreditRepository from "@/utilities/repositories/AdCredit";
import useApi from "@/hooks/useApi";
import formatNumberDecimal from "@/utils/format-number-decimal";
import { ECreditDeductionCategory } from "@/utilities/types/AdCredit";
import CategoryFilter from "@/components/ad-credits/CategoryFilter";
import cn from "@/utils/class-names";
import { IoIosArrowDown } from "react-icons/io";

export default function AdCredit() {
  const { id: userId } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReFetchHistory, setIsRefreshHistory] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<
    ECreditDeductionCategory[]
  >([]);
  const {
    request: getAdCreditInfoGeneralByUserId,
    response: userAdCreditInfo,
  } = useApi({
    request: AdCreditRepository.getAdCreditInfoGeneralByUserId,
  });

  const handleUpdateSuccess = () => {
    setIsRefreshHistory(true);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleRefreshHistory = (value: boolean) => {
    setIsRefreshHistory(value);
  };

  useEffect(() => {
    if (userId) {
      getAdCreditInfoGeneralByUserId(userId);
    }
  }, [userId, getAdCreditInfoGeneralByUserId]);

  useEffect(() => {
    if (isReFetchHistory && userId) {
      getAdCreditInfoGeneralByUserId(userId);
    }
  }, [isReFetchHistory, userId, getAdCreditInfoGeneralByUserId]);

  const refreshBalance = () => {
    if (userId) {
      getAdCreditInfoGeneralByUserId(userId);
    }
  };

  return (
    <div className="space-y-8 mt-4">
      {/* Update User Credit Button */}
      <div className="mb-6 flex justify-start items-center gap-8">
        <Button
          onClick={handleOpenModal}
          className="bg-black text-white hover:bg-gray-800 px-6 py-2 rounded-md"
        >
          Update User Credit
        </Button>
      </div>

      {/*  Package information */}
      <Accordion className="border rounded-md p-4">
        <Accordion.Header type="button">
          {({ open }) => (
            <div className="flex w-full cursor-pointer items-center justify-between font-semibold text-sm md:text-base">
              Package information
              <IoIosArrowDown
                className={cn(
                  "h-5 w-5 -rotate-0 transform transition-transform duration-300",
                  open && "-rotate-180",
                )}
              />
            </div>
          )}
        </Accordion.Header>
        <Accordion.Body>
          <UpdateUserCreditPackageForm />
        </Accordion.Body>
      </Accordion>

      <div className="mb-6 flex-col md:flex-row flex justify-between items-center gap-6">
        <StatBox
          label="Credit In"
          description="Top-ups and Bonuses"
          value={formatNumberDecimal(
            userAdCreditInfo?.data?.summary?.totalTopUp || 0,
          )}
          className="bg-green-100 text-green-500 w-full border border-green-500"
        />
        <StatBox
          label="Credit Out"
          description="Total Spending"
          value={formatNumberDecimal(
            userAdCreditInfo?.data?.summary.totalSpending || 0,
          )}
          className="bg-red-100 text-red-500 w-full border border-red-500"
        />
        <StatBox
          label="Current Balance"
          description="Available "
          value={formatNumberDecimal(
            userAdCreditInfo?.data?.summary?.netBalance || 0,
          )}
          className="bg-blue-100 text-blue-500 w-full border border-blue-500"
        />
        <StatBox
          label="Expired Credit"
          description="Credits past validity date"
          value={formatNumberDecimal(
            userAdCreditInfo?.data?.summary?.expiredCredit || 0,
          )}
          className="bg-gray-50 text-gray-500 w-full border border-gray-500  h-full"
        />
      </div>

      <div>
        <p className="text-sm md:text-base font-semibold mb-4">
          Expense Breakdown
        </p>
        <div className="mb-6 flex-col md:flex-row flex justify-between items-center gap-6">
          <StatBox
            label="Google Ads"
            value={formatNumberDecimal(
              Math.abs(
                userAdCreditInfo?.data?.categoryBreakdown?.["google-ads"]
                  .total || 0,
              ),
            )}
            className="bg-gray-50 w-full border border-gray-300"
          />
          <StatBox
            label="Lead"
            value={formatNumberDecimal(
              Math.abs(
                userAdCreditInfo?.data?.categoryBreakdown?.lead.total || 0,
              ),
            )}
            className="bg-gray-50 w-full border border-gray-300"
          />
          <StatBox
            label="Facebook"
            value={formatNumberDecimal(
              Math.abs(
                userAdCreditInfo?.data?.categoryBreakdown?.facebook.total || 0,
              ),
            )}
            className="bg-gray-50 w-full border border-gray-300"
          />
          <StatBox
            label="Ancillary"
            value={formatNumberDecimal(
              Math.abs(
                userAdCreditInfo?.data?.categoryBreakdown?.miscellaneous
                  .total || 0,
              ),
            )}
            className="bg-gray-50 w-full border border-gray-300"
          />
        </div>
      </div>
      <div className="space-y-6">
        {/* Filter by Category */}
        <CategoryFilter
          selectedCategories={selectedCategories}
          onCategoryChange={setSelectedCategories}
        />

        {/* User Credit History */}
        <AdCreditTransactions
          userId={userId}
          isReFetchHistory={isReFetchHistory}
          handleRefreshHistory={() => handleRefreshHistory(false)}
          categoryFilter={
            selectedCategories.length > 0 ? selectedCategories.join(",") : ""
          }
          onRevertSuccess={refreshBalance}
        />
      </div>
      {/* Update User Credit Modal */}
      <UpdateUserCreditModal
        userId={userId!}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleUpdateSuccess}
      />
    </div>
  );
}
